/**
 * request.ts
 * desc: custom api class that wraps fetch api with additional logic
 * note: automatically passes authentication headers and formats responses
 */
import { logout, getKeycloak, updateKeycloakToken } from './authentication';
import ApiRequestError, { type ApiRequestErrorParams } from './error';

// the isFile-mode payload; fileUrl is null when URL.createObjectURL throws
export interface FilePayload {
    fileUrl: string | null;
    blob: Blob;
}

// opt-in helper for the rare caller that reads the non-enumerable statusCode
// _enrichResponseObject defines on parsed object responses
export type WithStatusCode<T> = T & { statusCode: number };

// everything is a fetch RequestInit passthrough (signal, body, method, …);
// caller headers merge over the defaults, so any default header (even
// Authorization) can be overridden per call
// note: no getRawResponse/isFile here — those modes are the named functions
// requestRaw/requestFile below
export type RequestOpts = Omit<RequestInit, 'headers'> & {
    headers?: Record<string, string>;
};

// the mode flags stay internal for unconverted JS callers; TS callers use the
// named functions. The two flags are mutually exclusive — every call site sets
// at most one, and combining them is unsupported (file mode would skip the
// 4xx/5xx check that raw mode performs)
type RequestOptsInternal = RequestOpts & {
    getRawResponse?: boolean;
    isFile?: boolean;
};

// the finalized options handed to fetch (headers narrowed to a plain object)
type FetchOpts = Omit<RequestInit, 'headers'> & {
    headers: Record<string, string>;
};

let abortController = new AbortController();

/**
 * cancelRequest
 * desc: send abort signal and creates a new instance of AbortController
 * note: we need a new instance of AbortController per page
 *
 * @return AbortController instance
 */
function cancelRequest(): AbortController {
    abortController.abort();
    abortController = new AbortController();
    return abortController;
}

function request<T = unknown>(url = '', opts: RequestOpts = { }): Promise<T> {
    return _request(url, opts) as Promise<T>;
}

// raw-Response mode: caller reads headers / streams the body itself
export function requestRaw(url: string, opts: RequestOpts = { }): Promise<Response> {
    return _request(url, { ...opts, getRawResponse: true }) as Promise<Response>;
}

// file mode: resolves { fileUrl, blob }; note this path performs no 4xx/5xx
// check — preserved verbatim from the JS version
export function requestFile(url: string, opts: RequestOpts = { }): Promise<FilePayload> {
    return _request(url, { ...opts, isFile: true }) as Promise<FilePayload>;
}

function _request(url = '', opts: RequestOptsInternal = { }): Promise<unknown> {
    if (!url) throw new Error('API requests require an endpoint url');

    return updateKeycloakToken()
        .then(() => {
            const { headers, getRawResponse = false, isFile = false, ...rest } = opts;

            // define defaults, then let the caller's opts/headers override them
            // note: "credentials" property adds cookies to fetch requests
            const fetchOpts: FetchOpts = {
                credentials: 'include',
                method: 'GET',
                ...rest,
                headers: {
                    Accept: 'application/json',
                    Authorization: `Bearer ${getKeycloak()!.token}`,
                    'Content-Type': 'application/json',
                    ...headers,
                },
            };

            // multipart bodies: the browser must set Content-Type itself so it
            // carries the form boundary — this drops a caller-supplied value
            // too, since an explicit Content-Type on FormData is always a
            // missing-boundary bug
            if (fetchOpts.body instanceof FormData) {
                delete fetchOpts.headers['Content-Type'];
            }

            // finalize url
            // note: prefixes url w env hostname unless a full url was passed
            let fetchUrl = window.Ym?.API_URL ? `${window.Ym.API_URL}${url}` : url;
            // test for full url
            if (url.startsWith('http')) {
                fetchUrl = url;
            }

            if (fetchOpts.method === 'GET') {
                fetchOpts.signal = abortController.signal;
            }

            return fetch(fetchUrl, fetchOpts)
                .then(response => _checkStatusCode(response, isFile))
                .then(response => (getRawResponse ? response : _parseResponse(response, isFile)));
        });
}

/**
 * _checkStatusCode
 * desc: check status code for errors
 * note: the fetch API returns 4xx and 5xx as success
 * @param  response  API response
 * @param  isFile    file mode performs no 4xx/5xx check — preserved verbatim
 *                   from the JS version
 * @return           API response (or a rejection carrying ApiRequestError)
 */
function _checkStatusCode(response: Response, isFile: boolean): Response | Promise<never> {
    const unauthenticated = 401;
    const badRequest = 400;

    // if request unauthenticated, then logout of application
    // note: logout starts a redirect; the rejection below still reaches the caller
    if (response.status === unauthenticated) {
        logout();
    }

    if (response.status >= badRequest && !isFile) {
        return _throwApiError(response);
    }

    return response;
}

/**
 * _throwApiError
 * desc: turn an error response into a rejected ApiRequestError
 * note: parses the body as json, falling back to text; a json body of literal
 *       null also falls back, preserving the old pipeline's outcome ("null")
 */
async function _throwApiError(response: Response): Promise<never> {
    let result: unknown = null;
    try {
        // clone so the text fallback below can still read the body
        result = await response.clone().json();
    } catch {
        // not json — fall through to the text read
    }
    if (result === null) {
        result = await response.text();
    }

    // a string result reads its (undefined) properties below — matches the
    // untyped runtime behavior exactly, so the cast stays
    const body = result as ApiRequestErrorParams;
    const errorParams = {
        context: body.context,
        errorCode: body.errorCode,
        id: body.id,
        statusCode: response.status,
        url: response.url,
        internalDescription: body.internalDescription,
    };
    throw new ApiRequestError(typeof (result) === 'object' ? body.internalDescription || body.errorCode : result as string, errorParams);
}

/**
 * _parseResponse
 * desc: parse the body of the response (typically JSON)
 * note: the fetch API returns a file by default
 * @param  response  API response
 * @return           Response object
 */
function _parseResponse(response: Response, isFile: boolean): unknown {
    const noContent = 204;
    const responseDefault = {};

    // if response has content, then parse as json
    // note: using .json() method on empty body results in an error
    if (response.status !== noContent) {
        if (isFile) {
            return response.blob()
                .then(blob => {
                    try {
                        const fileUrl = URL.createObjectURL(blob);
                        return {
                            fileUrl,
                            blob
                        };
                    } catch {
                        return {
                            fileUrl: null,
                            blob
                        };
                    }
                });
        } else {
            // the catch also covers a json body of literal null, whose
            // enrichment throws — the body is then re-read as text
            // (preserved verbatim from the JS version)
            return response.clone().json()
                .then((result: unknown) => {
                    return _enrichResponseObject(response, result);
                })
                .catch(() => {
                    return response.text()
                        .then((result) => {
                            return _enrichResponseObject(response, result);
                        });
                });
        }
    }

    return _enrichResponseObject(response, responseDefault);
}

/**
 * _enrichResponseObject
 * desc: add additional properties to json response
 * note: allows to save http response info to check it later
 * @param  response  API response
 * @return           Response object
 */
function _enrichResponseObject(response: Response, json: unknown): unknown {
    // typeof null === 'object', so a JSON body of literal null reaches
    // defineProperty and throws — preserved verbatim from the JS version
    return typeof (json) === 'object' ?
        Object.defineProperty(json as object, 'statusCode', {
            value: response.status,
            writable: false
        })
        :
        json;
}

export { cancelRequest };
export default request;
